import { memo, useMemo } from "react";
import { useAppSelector } from "../../store/hooks/hooks";
import { Trans, useTranslation } from "react-i18next";
import Input from "../../components/common/Input";
import DropdownMenu, { type IDDItem } from "../../components/common/DropdownMenu";

interface ToolInputRowProps {
	inputId: string;
	dropdownItems: IDDItem[];
	onInputChange: (inputName: string, value: number | null) => void;
	onSelectUnit: (inputId: string, toUnitId: string) => void;
	disabled?: boolean;
}

export const InputRow: React.FC<ToolInputRowProps> = memo(({
	inputId,
	dropdownItems,
	onInputChange,
	onSelectUnit,
	disabled = false
}) => {
	const input = useAppSelector(state => {
		if (inputId.startsWith("well1"))
			return state.vlpCalculator.well1.inputs.find(i => i.id === inputId);

		if (inputId.startsWith("well2"))
			return state.vlpCalculator.well2.inputs.find(i => i.id === inputId);

		return state.vlpCalculator.inputs.find(i => i.id === inputId);
	});

	const { i18n } = useTranslation();
	const showHints = useAppSelector(state => state.settings.showHints);

	if (!input) {
		return null;
	}

	const tooltipContent = showHints ? (
		<Trans
			ns={'vlp'}
			key={i18n.language}
			i18nKey={`input_titles.${input.id}`}
			components={{
				sub: <sub />,
				sup: <sup />,
			}}
		/>
	) : undefined;

	const labelContent = (
		<Trans
			ns={"vlp"}
			key={i18n.language}
			i18nKey={`inputs.${input.id}`}
			components={{
				sub: <sub />,
				sup: <sup />,
			}}
		/>
	);

	const onDDSelectItem = (selectedItemId: string) => {
		onSelectUnit(input.id, selectedItemId);
	}

	const value: number | null = useMemo(() => {
		if (input.currentValue == null)
			return null;

		if (input.currentUnitId != null && input.currentUnitId == input.userUnitId)
			return input.userValue;

		return parseFloat(input.currentValue.toFixed(4));
	}, [input]);

	return (
		<div className="vlp__input-group">
			<label className="vlp__input-label" htmlFor={input.id}>
				{labelContent}:
			</label>
			<Input
				value={value}
				onChange={onInputChange}
				name={input.queryName}
				id={input.id}
				tooltip={tooltipContent}
				disabled={disabled}
				error={input.error}
				errorParams={input.errorParams}
			/>
			{input.unitCategoryId && input.currentUnitId && (
				<DropdownMenu
					inputId={input.id}
					items={dropdownItems}
					selectedItemId={input.currentUnitId}
					onSelect={onDDSelectItem}
					disabled={disabled}
				/>)
			}
		</div>
	);
});

InputRow.displayName = "VLPInputRow";
